import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  MenuItem,
  Select,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  Popper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  ClickAwayListener,
  DialogActions,
  Button,
  Snackbar,
  InputAdornment,
  InputLabel,
  Card,
  CardContent,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Fade,
  Grow,
  Zoom,
  Skeleton,
  Collapse,
  Avatar,
  Grid
} from '@mui/material';
import OrderLocationMap from '../components/OrderLocationMap';
import { Phone } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {
  CardGiftcard as CardGiftcardIcon,
  ShoppingBag as ShoppingBagIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Home as AddressIcon,
  CreditCard as PaymentIcon,
  CalendarToday as DateIcon,
  Update as UpdateIcon,
  LocalOffer as ProductIcon,
  AttachMoney as PriceIcon,
  FormatListNumbered as QuantityIcon,
  Discount as DiscountIcon,
  Savings as SavingsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import api from '../services/api'
import { useAuth } from '../context/AuthContext';
import AddProductToOrderModal from '../components/AddProductToOrderModal';
import TableManager from '../components/TableManager';
import { formatPrice } from '../components/CarrancyTrans';

const OrderDetails = ({keywordsData, language}) => {
  const { user } = useAuth();
  const { orderId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // State for inline editing
  const [editingItem, setEditingItem] = useState(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Table edit states
  const [tableEditDialogOpen, setTableEditDialogOpen] = useState(false);
  const [tableManagerOpen, setTableManagerOpen] = useState(false);

  const [availableTables, setAvailableTables] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [loadingTables, setLoadingTables] = useState(false);
  const [updatingTable, setUpdatingTable] = useState(false);
  const [tableError, setTableError] = useState('');
  const [quantityPopperAnchor, setQuantityPopperAnchor] = useState(null);
  // Mobile accordion state for sections
  const [expandedSections, setExpandedSections] = useState({
    deliveryPayment: true,
    customerInfo: true,
    orderTimeline: true,
    orderItems: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Function to print updated items (for edits and deletions)
const printUpdatedItems = async (orderId, action, itemsData) => {
  try {
    // First, fetch the current order to get printer configurations
    const currentOrder = await api.get(`/orders/${orderId}`);
    const originalPrinters = currentOrder.data.printers || {};
    
    // Create updated printers data based on the action
    const updatedPrinters = { ...originalPrinters };
    
    if (action === 'quantity_updated') {
      // For quantity updates, we need to update the item quantity in the printer data
      itemsData.forEach(updatedItem => {
        // Search through all printers
        Object.keys(updatedPrinters).forEach(printerId => {
          const printer = updatedPrinters[printerId];
          const itemIndex = printer.items.findIndex(item => item.product_id === updatedItem.product_id);
          
          if (itemIndex !== -1) {
            // Item found, update its quantity
            printer.items[itemIndex].quantity = updatedItem.new_quantity;
            printer.items[itemIndex].total_price = updatedItem.new_quantity * printer.items[itemIndex].unit_price;
          }
        });
      });
    } 
    else if (action === 'item_deleted') {
      // For deletions, remove the item from printer data
      itemsData.forEach(deletedItem => {
        Object.keys(updatedPrinters).forEach(printerId => {
          const printer = updatedPrinters[printerId];
          const itemIndex = printer.items.findIndex(item => item.product_id === deletedItem.product_id);
          
          if (itemIndex !== -1) {
            // Remove the item from printer items array
            printer.items.splice(itemIndex, 1);
          }
        });
      });
      
      // Also add a note to printers about canceled items
      Object.keys(updatedPrinters).forEach(printerId => {
        const printer = updatedPrinters[printerId];
        if (printer.items.length > 0) {
          // Add a cancel note for each deleted item (at printer level)
          printer.canceled_items = printer.canceled_items || [];
          itemsData.forEach(deletedItem => {
            printer.canceled_items.push({
              product_id: deletedItem.product_id,
              product_name: deletedItem.product_name,
              quantity: deletedItem.old_quantity,
              reason: 'Order item canceled/deleted',
              timestamp: new Date().toISOString()
            });
          });
        }
      });
    }
    
    // Send the updated printer data to a new endpoint
    const response = await api.post(`/admin/orders/${orderId}/print-update`, {
      action: action,
      printers: updatedPrinters,
      items: itemsData
    });
    
    return response.data;
  } catch (error) {
    console.error('Error printing updated items:', error);
    return null;
  }
};

  const fetchAvailableTables = async () => {
    setLoadingTables(true);
    try {
      const response = await api.get('/admin/cafe-tables', {
        params: { exclude_occupied: true }
      });
      const tablesData = response.data.tables || response.data;
      const tablesArray = Array.isArray(tablesData) ? tablesData : [];
      setAvailableTables(tablesArray);
    } catch (err) {
      console.error('Error fetching tables:', err);
      setTableError(language === 'ar' ? 'خطأ في تحميل الطاولات' : 'Error loading tables');
    } finally {
      setLoadingTables(false);
    }
  };


// Replace the handleOpenTableDialog function
const handleOpenTableDialog = () => {
    setTableManagerOpen(true);
};

// Update the updateOrderTable function to handle selected table
const updateOrderTable = async (selectedTable) => {
    setUpdatingTable(true);
    setTableError('');
    
    try {
        await api.put(`/admin/orders/${order.id}/table`, { table_id: selectedTable.id });
        
        setSnackbar({
            open: true,
            message: language === 'ar' 
                ? `تم نقل الطلب إلى الطاولة رقم ${selectedTable.table_number} بنجاح`
                : `Order moved to table ${selectedTable.table_number} successfully`,
            severity: 'success'
        });
        
        await fetchOrderDetails();
        setTableManagerOpen(false);
        
    } catch (err) {
        console.error('Error updating order table:', err);
        setSnackbar({
            open: true,
            message: err.response?.data?.message || 
                (language === 'ar' ? 'خطأ في تحديث رقم الطاولة' : 'Error updating table number'),
            severity: 'error'
        });
    } finally {
        setUpdatingTable(false);
    }
};


  const fetchOrderDetails = async () => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrderDetails();
    } catch (err) {
      console.error('Status update error:', err);
      showSnackbar(language === 'ar' ? 'خطأ في تحديث الحالة' : 'Error updating status', 'error');
    }
  };

const handleEditQuantity = async (item) => {
  if (!editQuantity || parseInt(editQuantity) < 1) {
    showSnackbar(language === 'ar' ? 'الكمية يجب أن تكون 1 على الأقل' : 'Quantity must be at least 1', 'error');
    return;
  }

  const oldQuantity = item.quantity;
  const newQuantity = parseInt(editQuantity);

  try {
    // Update quantity in backend
    const response = await api.put(`/admin/orders/${orderId}/items/${item.id}`, {
      quantity: newQuantity
    });
    
    setOrder(response.data);
    
    // Print the update (only for admin role)
    if ((user?.role === 'admin' || user?.role === 'casher') && order.status == 'processing') {
      await printUpdatedItems(orderId, 'quantity_updated', [{
        product_id: item.product.id,
        product_name: language === 'ar' ? item.product.name : item.product.name_en,
        old_quantity: oldQuantity,
        new_quantity: newQuantity,
        unit_price: item.price,
        notes: item.notes || null
      }]);
    }
    
    showSnackbar(language === 'ar' ? 'تم تحديث الكمية بنجاح' : 'Quantity updated successfully', 'success');
    setEditingItem(null);
    setEditQuantity('');
  } catch (err) {
    console.error('Error updating quantity:', err);
    showSnackbar(err.response?.data?.message || (language === 'ar' ? 'خطأ في تحديث الكمية' : 'Error updating quantity'), 'error');
  }
};

// Modified handleDeleteItem function with print functionality
const handleDeleteItem = async () => {
  if (!itemToDelete) return;

  try {
    // Store item info before deletion for printing
    const deletedItemInfo = {
      product_id: itemToDelete.product.id,
      product_name: language === 'ar' ? itemToDelete.product.name : itemToDelete.product.name_en,
      old_quantity: itemToDelete.quantity,
      unit_price: itemToDelete.price,
      total_price: itemToDelete.quantity * itemToDelete.price,
      notes: itemToDelete.notes || null,
      selected_size: itemToDelete.selected_size,
      selected_extras: itemToDelete.selected_extras
    };
    
    // Delete from backend
    const response = await api.delete(`/admin/orders/${orderId}/items/${itemToDelete.id}`);
    setOrder(response.data);
    
    // Print the deletion (only for admin role)
    if ((user?.role === 'admin' || user?.role === 'casher') && order.status == 'processing') {
      await printUpdatedItems(orderId, 'item_deleted', [deletedItemInfo]);
    }
    
    showSnackbar(language === 'ar' ? 'تم حذف المنتج بنجاح' : 'Item deleted successfully', 'success');
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  } catch (err) {
    console.error('Error deleting item:', err);
    showSnackbar(err.response?.data?.message || (language === 'ar' ? 'خطأ في حذف المنتج' : 'Error deleting item'), 'error');
  }
};

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'completed': return 'success';
      case 'canceled': return 'error';
      case 'succeeded': return 'success';
      case 'cancelled': return 'error';
      default: return 'primary';
    }
  };
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const statusTxt = {
    canceled: language === 'ar' ? 'ملغي' : 'Canceled',
    pending: language === 'ar' ? 'معلق' : 'Pending',
    processing: language === 'ar' ? 'قيد التحضير' : 'Preparation',
    shipped: language === 'ar' ? 'قيد التوصيل' : 'On road',
    delivered: language === 'ar' ? 'مكتمل' : 'Complete'
  };

  const formatPaymentMethod = (method) => {
    return method.replace(/_/g, ' ');
  };

  const totalSavings = order?.items?.reduce((total, item) => {
    const originalPrice = item.original_price || item.price;
    return total + (originalPrice - item.price) * item.quantity;
  }, 0) || 0;

  const originalTotal = order?.items?.reduce((total, item) => {
    return total + (item.original_price || item.price) * item.quantity;
  }, 0) || 0;

  // Mobile Order Item Card Component
  const OrderItemCard = ({ item }) => {
    const originalPrice = item.original_price || item.price;
    const isDiscounted = item.discount_amount > 0;
    const discountAmount = item.discount_amount || 0;
    const discountPercent = item.discount_percent || 0;
    const isFreeGift = item.is_free_item || item.free_reward_item_id;
    const hasPromotion = item.applied_promotion_data;
    const promotionData = item.applied_promotion_data;
    const promotionRewardName = promotionData?.reward_name;
    const qualifyingAmount = promotionData?.qualifying_amount || item.promotion_qualifying_amount;
    const [expanded, setExpanded] = useState(false);

    return (
      <Grow in>
        <Card sx={{ 
          mb: 2, 
          bgcolor: '#0D0D0D', 
          border: '1px solid #2D2D2D',
          borderRadius: 2,
          '&:hover': { borderColor: '#FFD700' }
        }}>
          <CardContent sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              {/* Product Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <ProductIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ color: '#FFFFFF', fontWeight: 600, flex: 1 }}>
                    {language === 'ar' ? item.product.name : item.product.name_en}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {item.has_customizations && (
                    <Chip
                      label={language === 'ar' ? 'مخصص' : 'Custom'}
                      size="small"
                      sx={{ 
                        bgcolor: '#FFD700',
                        color: '#000000',
                        fontWeight: 'bold',
                        height: 20,
                        fontSize: '0.65rem'
                      }}
                    />
                  )}
                  {isFreeGift && (
                    <Chip
                      icon={<CardGiftcardIcon sx={{ fontSize: 12 }} />}
                      label={language === 'ar' ? 'هدية' : 'Gift'}
                      size="small"
                      sx={{ 
                        bgcolor: '#4CAF50',
                        color: '#FFFFFF',
                        height: 20,
                        fontSize: '0.65rem'
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Item Details */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QuantityIcon sx={{ color: '#B3B3B3', fontSize: 16 }} />
                  <Typography sx={{ color: '#FFFFFF' }}>x{item.quantity}</Typography>
                  {order.status !== 'delivered' && order.status !== 'canceled' && editingItem !== item.id && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingItem(item.id);
                        setEditQuantity(item.quantity.toString());
                      }}
                      sx={{ color: '#FFD700' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  {isFreeGift ? (
                    <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                      {language === 'ar' ? 'مجاني' : 'FREE'}
                    </Typography>
                  ) : (
                    <Typography sx={{ color: '#FFD700', fontWeight: 600 }}>
                      {formatPrice((item.quantity * parseFloat(item.price)).toFixed(2),order.currency,language)}
                    </Typography>
                  )}
                  {isDiscounted && !isFreeGift && (
                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: '#B3B3B3', display: 'block' }}>
                      {formatPrice(originalPrice.toFixed(2),order.currency,language)}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Price Details */}
              {isDiscounted && !isFreeGift && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'سعر الوحدة' : 'Unit price'}:
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                    {formatPrice(item.price,order.currency,language)}
                  </Typography>
                </Box>
              )}

              {/* Customizations */}
              {item.has_customizations && (
                <>
                  <Divider sx={{ borderColor: '#2D2D2D' }} />
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ color: '#FFD700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
                      onClick={() => setExpanded(!expanded)}
                    >
                      {language === 'ar' ? 'تفاصيل التخصيص' : 'Customization Details'}
                      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Typography>
                    <Collapse in={expanded}>
                      <Box sx={{ mt: 1, pl: 1 }}>
                        {item.selected_size && (
                          <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block' }}>
                            {language === 'ar' ? 'مقاس' : 'Size'}: {language === 'ar' ? item.selected_size.name_ar || item.selected_size.name : item.selected_size.name_en || item.selected_size.name}
                          </Typography>
                        )}
                        {item.selected_extras && item.selected_extras.length > 0 && (
                          <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block' }}>
                            {language === 'ar' ? 'إضافات' : 'Extras'}: {item.selected_extras.map(e => language === 'ar' ? e.name_ar || e.name : e.name_en || e.name).join(', ')}
                          </Typography>
                        )}
                        {item.customization_price > 0 && !isFreeGift && (
                          <Typography variant="caption" sx={{ color: '#4CAF50', display: 'block' }}>
                            +{formatPrice(item?.customization_price,order.currency,language)}
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </>
              )}

              {/* Promotion Info */}
              {hasPromotion && !isFreeGift && (
                <Alert severity="info" icon={<CardGiftcardIcon />} sx={{ py: 0, bgcolor: 'rgba(255, 152, 0, 0.1)' }}>
                  <Typography variant="caption">
                    {language === 'ar' 
                      ? `أنفق ${qualifyingAmount || ''} ر.م إضافية واحصل على ${promotionRewardName || 'هدية'} مجاناً`
                      : `Spend ${qualifyingAmount || ''} R.m extra and get free ${promotionRewardName || 'gift'}`}
                  </Typography>
                </Alert>
              )}

              {/* Delete Action */}
              {order.status !== 'delivered' && order.status !== 'canceled' && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      setItemToDelete(item);
                      setDeleteDialogOpen(true);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    {language === 'ar' ? 'حذف' : 'Delete'}
                  </Button>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grow>
    );
  };

  // Edit Quantity Dialog for Mobile
  if (editingItem && isMobile) {
    const editingItemData = order?.items?.find(i => i.id === editingItem);
    return (
      <Dialog open={true} onClose={() => setEditingItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#FFD700' }}>
          {language === 'ar' ? 'تعديل الكمية' : 'Edit Quantity'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="number"
            fullWidth
            label={language === 'ar' ? 'الكمية' : 'Quantity'}
            value={editQuantity}
            onChange={(e) => setEditQuantity(e.target.value)}
            inputProps={{ min: 1 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingItem(null)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={() => handleEditQuantity(editingItemData)} variant="contained" sx={{ bgcolor: '#FFD700', color: '#000' }}>
            {language === 'ar' ? 'تحديث' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={60} sx={{ bgcolor: '#1A1A1A' }} />
          <Skeleton variant="rounded" height={200} sx={{ bgcolor: '#1A1A1A' }} />
          <Skeleton variant="rounded" height={300} sx={{ bgcolor: '#1A1A1A' }} />
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5">{language === 'ar' ? 'لم يتم العثور على الطلب' : 'Order not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 10, maxWidth: 1400, mx: 'auto' }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>{language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {language === 'ar' 
              ? `هل أنت متأكد من حذف ${itemToDelete?.product?.name || 'هذا المنتج'} من الطلب؟`
              : `Are you sure you want to delete ${itemToDelete?.product?.name_en || 'this item'} from the order?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleDeleteItem} color="error" variant="contained">
            {language === 'ar' ? 'حذف' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate(user?.role === 'admin' ? '/admin/orders' : '/casher/orders')} 
            sx={{ 
              bgcolor: 'rgba(255, 215, 0, 0.1)', 
              '&:hover': { bgcolor: 'rgba(255, 215, 0, 0.2)' } 
            }}
          >
            {language === 'ar' ? <ArrowForwardIcon /> : <ArrowBackIcon />}
          </IconButton>
          <Avatar sx={{ bgcolor: '#FFD700', color: '#000' }}>
            <ShoppingBagIcon />
          </Avatar>
          <Box>
            <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 700 }}>
              # {language === 'ar' ? 'طلب رقم' : 'Order ID'} {order.id}
            </Typography>
          </Box>
          <Chip
            label={statusTxt[order.status]}
            color={getStatusColor(order.status)}
            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
          />
        </Box>
      </Box>

      {/* Savings Banners */}
      {totalSavings > 0 && (
        <Zoom in>
          <Paper elevation={2} sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3, 
            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
            color: 'white',
            borderRadius: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <SavingsIcon sx={{ fontSize: { xs: 30, sm: 40 } }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {language === 'ar' ? '🎉 العميل قام بتوفير ' : '🎉 The customer saved '} 
                  {formatPrice(totalSavings.toFixed(2),order.currency,language)}
                </Typography>
                <Typography variant="caption">
                  {language === 'ar' ? 'تبلغ نسبة الخصم ' : 'The discount rate '} 
                  {((totalSavings / originalTotal) * 100).toFixed(1)} {language === 'ar' ? '% من هاذا الطلب' : '% from this order'} 
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Zoom>
      )}

      {order.points_used > 0 && (
        <Zoom in>
          <Paper elevation={2} sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3, 
            background: 'linear-gradient(135deg, #72671e 0%, #7e8f10 100%)',
            color: 'white',
            borderRadius: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <AccountBalanceWalletIcon sx={{ fontSize: { xs: 30, sm: 40 } }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {language === 'ar' ? '🎉 العميل قام بإستخدام ' : '🎉 The customer used '} 
                  {order.points_used} {language === 'ar' ? ' نقطة' : ' Point'}!
                </Typography>
                <Typography variant="caption">
                  {language === 'ar' ? 'تبلغ قيمة الخصم ' : 'The discount value '} 
                  {formatPrice(order.points_used_value.toFixed(2),order.currency,language)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Zoom>
      )}

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Delivery & Payment Section */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ 
            p: 2, 
            borderRadius: 2,
            bgcolor: '#0D0D0D',
            border: '1px solid #2D2D2D'
          }}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: isMobile ? 'pointer' : 'default',
                mb: 2
              }}
              onClick={() => isMobile && toggleSection('deliveryPayment')}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#FFD700' }}>
                <AddressIcon /> 
                {language === 'ar' ? 'معلومات التوصيل / الدفع' : 'Delivery/Payment Info'}
              </Typography>
              {isMobile && (expandedSections.deliveryPayment ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </Box>
            
            <Collapse in={!isMobile || expandedSections.deliveryPayment}>
              <Stack spacing={2}>
                <Box sx={{ height: { xs: 200, sm: 250, md: 300 } }}>
                  <OrderLocationMap
                    latitude={order.latitude}
                    longitude={order.longitude}
                    height="100%"
                  />
                </Box>
                
                <Divider sx={{ borderColor: '#2D2D2D' }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LocationOnIcon sx={{ color: '#FFD700' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'عنوان التوصيل' : 'Delivery address'}
                    </Typography>
                    <Typography sx={{ color: '#FFFFFF' }}>{order.shipping_address}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonIcon sx={{ color: '#FFD700' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'عدد الأشخاص' : 'Pax No'}
                    </Typography>
                    <Typography sx={{ color: '#FFFFFF' }}>{order.pax_no}</Typography>
                  </Box>
                </Box>
                
                {order.table_id > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TableRestaurantIcon sx={{ color: '#FFD700' }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                          {language === 'ar' ? 'رقم الطاولة' : 'Table Number'}
                        </Typography>
                        <Typography sx={{ color: '#FFFFFF' }}>
                          {language === 'ar' ? `طاولة رقم ${order.table_number}` : `Table ${order.table_number}`}
                        </Typography>
                      </Box>
                    </Box>
                    {order.status !== 'delivered' && order.status !== 'canceled' && (
                      <IconButton size="small" onClick={handleOpenTableDialog} sx={{ color: '#FFD700' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                )}
                
                <Divider sx={{ borderColor: '#2D2D2D' }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PaymentIcon sx={{ color: '#FFD700' }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                        {language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}
                      </Typography>
                      <Typography sx={{ color: '#FFFFFF' }}>{formatPaymentMethod(order.payment_method)}</Typography>
                    </Box>
                  </Box>
                  {order.visa_payment_transaction ? (
                    <Chip label={order.visa_payment_transaction.status} color={getStatusColor(order.visa_payment_transaction.status)} size="small" />
                  ) : (
                    <Chip label={order.payment_method === 'cash_on_delivery' ? 'Cash' : 'No Payment'} size="small" />
                  )}
                </Box>
                
                <FormControl size="small" fullWidth>
                  <Select
                    disabled={order.status === 'delivered' || order.status === 'canceled'}
                    value={order.status}
                    onChange={(e) => updateStatus(order.id, e.target.value)}
                    sx={{ color: '#FFFFFF' }}
                  >
                    <MenuItem value="pending">{language === 'ar' ? 'معلقة' : 'Pending'}</MenuItem>
                    <MenuItem value="processing">{language === 'ar' ? 'قيد التحضير' : 'Preparation'}</MenuItem>
                    <MenuItem value="shipped">{language === 'ar' ? 'قيد التوصيل' : 'On road'}</MenuItem>
                    <MenuItem sx={{ color: '#3ad00d'}} value="delivered">{language === 'ar' ? 'مكتمل' : 'Complete'}</MenuItem>
                    <MenuItem sx={{ color: '#eb3107'}} value="canceled">{language === 'ar' ? 'ملغي' : 'Canceled'}</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Collapse>
          </Paper>
        </Grid>

        {/* Customer Information Section */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ 
            p: 2, 
            borderRadius: 2,
            bgcolor: '#0D0D0D',
            border: '1px solid #2D2D2D'
          }}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: isMobile ? 'pointer' : 'default',
                mb: 2
              }}
              onClick={() => isMobile && toggleSection('customerInfo')}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#FFD700' }}>
                <PersonIcon /> {language === 'ar' ? 'معلومات العميل' : 'Customer Information'}
              </Typography>
              {isMobile && (expandedSections.customerInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </Box>
            
            <Collapse in={!isMobile || expandedSections.customerInfo}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonIcon sx={{ color: '#FFD700' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'إسم العميل' : 'Customer Name'}
                    </Typography>
                    <Typography sx={{ color: '#FFFFFF' }}>{order.user?.username}</Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <EmailIcon sx={{ color: '#FFD700' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'الإيميل' : 'Customer E-mail'}
                    </Typography>
                    <Typography sx={{ color: '#FFFFFF' }}>{order.user?.email}</Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Phone sx={{ color: '#FFD700' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'رقم الهاتف' : 'Phone No'}
                    </Typography>
                    <Typography sx={{ color: '#FFFFFF' }}>{order.user?.user_phone}</Typography>
                  </Box>
                </Box>
              </Stack>
            </Collapse>
          </Paper>
        </Grid>

        {/* Order Timeline Section */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ 
            p: 2, 
            borderRadius: 2,
            bgcolor: '#0D0D0D',
            border: '1px solid #2D2D2D'
          }}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: isMobile ? 'pointer' : 'default',
                mb: 2
              }}
              onClick={() => isMobile && toggleSection('orderTimeline')}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#FFD700' }}>
                <AccessTimeIcon /> {language === 'ar' ? 'الجدول الزمني' : 'Order Timeline'}
              </Typography>
              {isMobile && (expandedSections.orderTimeline ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </Box>
            
            <Collapse in={!isMobile || expandedSections.orderTimeline}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DateIcon sx={{ color: '#FFD700' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'تم إنشاءه' : 'Created at'}
                    </Typography>
                    <Typography sx={{ color: '#FFFFFF' }}>{new Date(order.created_at).toLocaleString()}</Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <UpdateIcon sx={{ color: '#FFD700' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'أخر تحديث' : 'Last update'}
                    </Typography>
                    <Typography sx={{ color: '#FFFFFF' }}>{new Date(order.updated_at).toLocaleString()}</Typography>
                  </Box>
                </Box>
              </Stack>
            </Collapse>
          </Paper>
        </Grid>

        {/* Order Items Section */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            borderRadius: 2,
            bgcolor: '#0D0D0D',
            border: '1px solid #2D2D2D'
          }}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: isMobile ? 'pointer' : 'default',
                mb: 2
              }}
              onClick={() => isMobile && toggleSection('orderItems')}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#FFD700' }}>
                <ShoppingBagIcon /> {language === 'ar' ? 'محتوى السلة' : 'Basket Contents'}
              </Typography>
              <Button
  variant="contained"
  disabled={order.status === 'delivered' || order.status === 'canceled' }
  startIcon={<AddIcon />}
  onClick={() => setAddProductModalOpen(true)}
  sx={{
    backgroundColor: '#FFD700',
    color: '#000000',
    '&:hover': { backgroundColor: '#FFE55C' }
  }}
>
  {language === 'ar' ? 'إضافة منتج' : 'Add Product'}
</Button>
              {isMobile && (expandedSections.orderItems ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </Box>
            
            <Collapse in={!isMobile || expandedSections.orderItems}>
              {isMobile ? (
                // Mobile View - Cards
                <Stack spacing={2}>
                  {order.items.map(item => (
                    <OrderItemCard key={item.id} item={item} />
                  ))}
                  
                  {/* Mobile Summary */}
                  <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid #2D2D2D', borderRadius: 2 }}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        {totalSavings > 0 && (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                                {language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}:
                              </Typography>
                              <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                                {originalTotal.toFixed(2)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                                {language === 'ar' ? 'الخصم' : 'Discount'}:
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                                -{totalSavings.toFixed(2)}
                              </Typography>
                            </Box>
                          </>
                        )}
                        
                        {order.points_used > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                              {language === 'ar' ? 'خصم النقاط' : 'Points Discount'}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                              -{order.points_used_value.toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        
                        {order.customization_summary?.total_customized_items > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#FFD700' }}>
                              {language === 'ar' ? 'الإضافات' : 'Extras'}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFD700' }}>
                              +{order.customization_summary.total_customization_price}
                            </Typography>
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                            {language === 'ar' ? 'رسوم الخدمة' : 'Service charge'}:
                          </Typography>
                          <Typography variant="body2">{order.service_charge}</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                            {language === 'ar' ? 'رسوم التوصيل' : 'Delivery fees'}:
                          </Typography>
                          <Typography variant="body2">{order.delivery_fee}</Typography>
                        </Box>
                        
                        <Divider sx={{ borderColor: '#2D2D2D' }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#FFFFFF' }}>
                            {language === 'ar' ? 'الإجمالي' : 'Total'}:
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                            {formatPrice(order.total_amount,order.currency,language)}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Stack>
              ) : (
                // Desktop View - Table
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#1A1A1A' }}>
                        <TableCell sx={{ color: '#FFD700' }}>{language === 'ar' ? 'إسم المنتج' : 'Product Name'}</TableCell>
                        <TableCell align="center" sx={{ color: '#FFD700' }}>{language === 'ar' ? 'الكمية' : 'Quantity'}</TableCell>
                        <TableCell align="right" sx={{ color: '#FFD700' }}>{language === 'ar' ? 'سعر الحبة' : 'Unit price'}</TableCell>
                        <TableCell align="right" sx={{ color: '#FFD700' }}>{language === 'ar' ? 'خصم' : 'Discount'}</TableCell>
                        <TableCell align="right" sx={{ color: '#FFD700' }}>{language === 'ar' ? 'الإجمالي' : 'Total'}</TableCell>
                        <TableCell align="center" sx={{ color: '#FFD700' }}>{language === 'ar' ? 'إجراءات' : 'Actions'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map(item => {
                        const originalPrice = item.original_price || item.price;
                        const isDiscounted = item.discount_amount > 0;
                        const discountAmount = item.discount_amount || 0;
                        const discountPercent = item.discount_percent || 0;
                        const isFreeGift = item.is_free_item || item.free_reward_item_id;
                        const hasPromotion = item.applied_promotion_data;
                        const promotionData = item.applied_promotion_data;
                        const promotionRewardName = promotionData?.reward_name;
                        const qualifyingAmount = promotionData?.qualifying_amount || item.promotion_qualifying_amount;

                        return (
                          <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#1A1A1A' } }}>
                            <TableCell>
                              {/* Same desktop table content as original - preserved */}
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <ProductIcon color="action" sx={{ mr: 1 }} />
                                  <Typography component="span" sx={{ color: '#FFFFFF' }}>
                                    {language === 'ar' ? item.product.name : item.product.name_en}
                                  </Typography>
                                  
                                  {item.has_customizations && (
                                    <Chip label={language === 'ar' ? 'مخصص' : 'Custom'} size="small" sx={{ backgroundColor: '#FFD700', color: '#000000', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
                                  )}
                                  
                                  {hasPromotion && !isFreeGift && (
                                    <Chip icon={<CardGiftcardIcon sx={{ fontSize: 14 }} />} label={language === 'ar' ? 'يؤهل لهدية' : 'Qualifies for Gift'} size="small" sx={{ backgroundColor: '#FF9800', color: '#FFFFFF', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
                                  )}
                                  
                                  {isFreeGift && (
                                    <Chip icon={<CardGiftcardIcon sx={{ fontSize: 14 }} />} label={language === 'ar' ? 'هدية مجانية' : 'Free Gift'} size="small" sx={{ backgroundColor: '#4CAF50', color: '#FFFFFF', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
                                  )}
                                </Box>
                                
                                {item.has_customizations && (
                                  <Box sx={{ mt: 0.5, ml: 4 }}>
                                    <Typography variant="caption" sx={{ color: '#FFD700', display: 'block' }}>
                                      {item.selected_size && (
                                        <span>{language === 'ar' ? 'مقاس' : 'Size'}: {language === 'ar' ? item.selected_size.name_ar || item.selected_size.name : item.selected_size.name_en || item.selected_size.name} </span>
                                      )}
                                      {item.selected_extras && item.selected_extras.length > 0 && (
                                        <span>
                                          {item.selected_size ? '| ' : ''}
                                          {language === 'ar' ? 'إضافات' : 'Extras'}: {item.selected_extras.map(e => language === 'ar' ? e.name_ar || e.name : e.name_en || e.name).join(', ')}
                                        </span>
                                      )}
                                    </Typography>
                                    {item.customization_price > 0 && !isFreeGift && (
                                      <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                                        +{formatPrice(item.customization_price,order.currency,language)}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                
                                {hasPromotion && !isFreeGift && (
                                  <Box sx={{ mt: 0.5, ml: 4 }}>
                                    <Typography variant="caption" sx={{ color: '#FF9800', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CardGiftcardIcon sx={{ fontSize: 14 }} />
                                      {language === 'ar' 
                                        ? `أنفق ${qualifyingAmount || ''} ر.م إضافية واحصل على ${promotionRewardName || 'هدية'} مجاناً` 
                                        : `Spend ${qualifyingAmount || ''} R.m extra and get free ${promotionRewardName || 'gift'}`}
                                    </Typography>
                                  </Box>
                                )}
                                
                                {isFreeGift && promotionData && (
                                  <Box sx={{ mt: 0.5, ml: 4 }}>
                                    <Typography variant="caption" sx={{ color: '#4CAF50', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CardGiftcardIcon sx={{ fontSize: 14 }} />
                                      {language === 'ar' 
                                        ? `هدية من عرض ${promotionRewardName || ''}`
                                        : `Gift from ${promotionRewardName || ''} promotion`}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            
     <TableCell align="center">
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
    <QuantityIcon color="action" />
    <Typography 
      sx={{ 
        color: '#FFFFFF',
        cursor: 'pointer',
        '&:hover': { color: '#FFD700', textDecoration: 'underline' }
      }}
      onClick={(e) => {
        if (order.status !== 'delivered' && order.status !== 'canceled') {
          setEditingItem(item.id);
          setEditQuantity(item.quantity.toString());
          setQuantityPopperAnchor(e.currentTarget);
        }
      }}
    >
      {item.quantity}
    </Typography>
    {order.status !== 'delivered' && order.status !== 'canceled' && (
      <IconButton 
        size="small" 
        onClick={(e) => { 
          setEditingItem(item.id); 
          setEditQuantity(item.quantity.toString());
          setQuantityPopperAnchor(e.currentTarget);
        }} 
        sx={{ ml: 1, color: '#FFD700' }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    )}
  </Box>

  {/* Quantity Edit Popper */}
  <Popper
    open={editingItem === item.id && Boolean(quantityPopperAnchor)}
    anchorEl={quantityPopperAnchor}
    placement="bottom-start"
    transition
    sx={{ zIndex: 1300 }}
  >
    {({ TransitionProps }) => (
      <Fade {...TransitionProps} timeout={200}>
        <Paper
          elevation={8}
          sx={{
            p: 1.5,
            mt: 1,
            backgroundColor: '#1A1A1A',
            border: '1px solid #FFD700',
            borderRadius: '12px'
          }}
        >
          <ClickAwayListener onClickAway={() => {
            setEditingItem(null);
            setEditQuantity('');
            setQuantityPopperAnchor(null);
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                type="number"
                value={editQuantity}
                onChange={(e) => setEditQuantity(e.target.value)}
                onKeyPress={(e) => { 
                  if (e.key === 'Enter') {
                    handleEditQuantity(item);
                    setQuantityPopperAnchor(null);
                  }
                }}
                InputProps={{
                  inputProps: { min: 1, style: { textAlign: 'center' } },
                }}
                autoFocus
                sx={{ width: 80 }}
              />
              <IconButton 
                size="small" 
                onClick={() => {
                  handleEditQuantity(item);
                  setQuantityPopperAnchor(null);
                }} 
                sx={{ color: '#FFD700' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => {
                  setEditingItem(null);
                  setEditQuantity('');
                  setQuantityPopperAnchor(null);
                }} 
                sx={{ color: '#FF3D00' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </ClickAwayListener>
        </Paper>
      </Fade>
    )}
  </Popper>
</TableCell>
                            
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <PriceIcon color="action" sx={{ mr: 1 }} />
                                {isDiscounted ? (
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="body2" color="error" fontWeight="bold">
                                      {formatPrice(item.price,order.currency,language)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                      {formatPrice(originalPrice.toFixed(2),order.currency,language)}
                                    </Typography>
                                  </Box>
                                ) : (
                                  `${formatPrice(item.price,order.currency,language)}`
                                )}
                              </Box>
                            </TableCell>
                            
                            <TableCell align="right">
                              {isDiscounted ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                  <DiscountIcon color="success" sx={{ mr: 0.5 }} />
                                  <Typography variant="body2" color="success.main">
                                    - {formatPrice(discountAmount.toFixed(2),order.currency,language)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    ({discountPercent}%)
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            
                            <TableCell align="right">
                              {isFreeGift ? (
                                <>
                                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#FFFFFF' }}>
                                    {formatPrice(item.quantity * parseFloat(item.price).toFixed(2),order.currency,language)}
                                  </Typography>
                                  {isDiscounted && (
                                    <Typography variant="caption" color="success.main" display="block">
                                      {language === 'ar' ? 'وفر' : 'Saved'} {formatPrice((discountAmount * item.quantity).toFixed(2),order.currency,language)}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                                    {language === 'ar' ? 'مجاني' : 'FREE'}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#FFFFFF' }}>
                                    {formatPrice((item.quantity * parseFloat(item.price)).toFixed(2),order.currency,language)} 
                                  </Typography>
                                  {isDiscounted && (
                                    <Typography variant="caption" color="success.main" display="block">
                                      {language === 'ar' ? 'وفر' : 'Saved'} {formatPrice((discountAmount * item.quantity).toFixed(2),order.currency,language)}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </TableCell>
                            
                            <TableCell align="center">
                              {order.status !== 'delivered' && order.status !== 'canceled' && (
                                <IconButton color="error" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }} size="small">
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      
                      {/* Desktop Summary Rows - Same as original */}
                      {order.items.some(item => item.applied_promotion_data) && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ backgroundColor: 'rgba(255, 215, 0, 0.05)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, flexWrap: 'wrap' }}>
                              <CardGiftcardIcon sx={{ color: '#FFD700' }} />
                              <Typography variant="subtitle2" sx={{ color: '#FFD700' }}>
                                {language === 'ar' ? 'العروض الترويجية المطبقة' : 'Applied Promotions'}:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {[...new Set(order.items.filter(item => item.applied_promotion_data).map(item => item.applied_promotion_data.reward_name))].map((rewardName, index) => (
                                  <Chip key={index} size="small" label={rewardName || (language === 'ar' ? 'عرض' : 'Promotion')} sx={{ backgroundColor: '#FFD700', color: '#000000', fontWeight: 600 }} />
                                ))}
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                      
                      {totalSavings > 0 && (
                        <>
                          <TableRow>
                            <TableCell colSpan={5} align="right"><Typography variant="body2" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'المجموع الفرعي (الأصلي)' : 'Subtotal (original)'}:</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" sx={{ textDecoration: 'line-through' }}>{formatPrice(originalTotal.toFixed(2),order.currency,language)}</Typography></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={5} align="right"><Typography variant="body2" color="success.main"><DiscountIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />{language === 'ar' ? 'إجمالي الخصم' : 'Total Discount'} :</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" color="success.main">- {formatPrice(totalSavings.toFixed(2),order.currency,language)}</Typography></TableCell>
                          </TableRow>
                        </>
                      )}
                      
                      {order.points_used > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="right"><Typography variant="body2" color="success.main"><DiscountIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />{language === 'ar' ? 'إجمالي خصم النقاط' : 'Total Points Discount'} :</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" color="success.main">- {formatPrice(order.points_used_value.toFixed(2),order.currency,language)}</Typography></TableCell>
                        </TableRow>
                      )}
                      
                      {order.free_items_count > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="right"><Typography variant="body2" sx={{ color: '#4CAF50', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}><CardGiftcardIcon sx={{ fontSize: 16 }} />{language === 'ar' ? 'العناصر المجانية' : 'Free Items'} :</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>{order.free_items_count} {language === 'ar' ? 'عنصر' : 'item(s)'}</Typography></TableCell>
                        </TableRow>
                      )}
                      
                      {order.customization_summary?.total_customized_items > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="right"><Typography variant="body2" sx={{ color: '#FFD700' }}><Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}><span>✨</span>{language === 'ar' ? 'إجمالي قيمة الإضافات' : 'Total Extras Value'}:</Box></Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>+{formatPrice(order.customization_summary.total_customization_price,order.currency,language)}</Typography></TableCell>
                        </TableRow>
                      )}
                      
                      <TableRow>
                        <TableCell colSpan={5} align="right"><Typography variant="body2" sx={{ color: '#B3B3B3' }}><Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>{language === 'ar' ? 'رسوم الخدمة' : 'Service charge'}:</Box></Typography></TableCell>
                        <TableCell align="right"><Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>{formatPrice(order.service_charge,order.currency,language)}</Typography></TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell colSpan={5} align="right"><Typography variant="body2" sx={{ color: '#B3B3B3' }}><Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>{language === 'ar' ? 'رسوم التوصيل' : 'Delivery fees'} :</Box></Typography></TableCell>
                        <TableCell align="right"><Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>{formatPrice(order.delivery_fee,order.currency,language)}</Typography></TableCell>
                      </TableRow>
                      
                      <TableRow sx={{ '& td': { borderBottom: 'none' } }}>
                        <TableCell colSpan={5} align="right"><Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#FFFFFF' }}>{language === 'ar' ? 'إجمالي الطلب' : 'Total order'} :</Typography></TableCell>
                        <TableCell align="right"><Typography variant="h6" color={totalSavings > 0 ? 'success.main' : '#FFD700'} fontWeight="bold">{formatPrice(order.total_amount,order.currency,language)}</Typography></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Collapse>
          </Paper>
        </Grid>
      </Grid>

      {/* Table Edit Dialog */}
<TableManager
    open={tableManagerOpen}
    onClose={() => setTableManagerOpen(false)}
    onSelectTable={updateOrderTable}
    currentTableId={order?.table_id}
    language={language}
/>
<AddProductToOrderModal
  open={addProductModalOpen}
  onClose={() => setAddProductModalOpen(false)}
  orderId={orderId}
  onProductAdded={async () => {
    // This will be called after product is added and printed
    await fetchOrderDetails(); // Refresh order details
    setAddProductModalOpen(false);
  }}
  language={language}
  keywordsData={keywordsData}
  user={user} // Pass user to check for admin role
  isProssessing= {order.status === 'processing'}
/>
    </Box>
  );
};

export default OrderDetails;